/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { ChevronDown, Check, Zap, Brain, Stars, Box, Hexagon } from 'lucide-react';
import { AiModel, AVAILABLE_MODELS } from '../../lib/ai/models';

interface ModelSelectorProps {
    selectedModelId: string;
    onSelect: (modelId: string) => void;
}

// --- Brand Icons ---

const OpenAIIcon = ({ className }: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a1.5458 1.5458 0 0 1 .8312 1.3198v5.1773a4.485 4.485 0 0 1-5.2877 3.6319zm3.6058-9.3081-6.2225-3.592a.8034.8034 0 0 0-.784 0l-2.0154 1.1639a1.5458 1.5458 0 0 1-1.5713-.0333L6.2769 7.893a4.485 4.485 0 0 1 7.3916-2.102l-4.7782 2.7582a.7948.7948 0 0 0-.3927.6813v3.8893zM5.2283 16.496l1.3938-.8091v-5.2638l-4.7782 2.7581a.7948.7948 0 0 0-.3927.6813v1.6445a4.485 4.485 0 0 1 3.7771-4.2748z" />
    </svg>
);

const AnthropicIcon = ({ className }: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d="M17.47 5.196L12.006.51 6.542 5.196H3.63L12.006 23.49l8.364-18.294h-2.9ZM12.006 4.383l3.654 3.129H8.352l3.654-3.129Zm-5.32 6.079h2.37l-1.185-3.92-1.185 3.92Zm7.69 0h2.37l-1.185-3.92-1.185 3.92ZM7.746 12.41l-.922 3.037H4.372l3.374-11.115 2.128 7.039-2.128 1.04Zm8.508 0l2.128-1.04 2.128-7.039 3.374 11.115h-2.452l-.922-3.037h-4.256Z" />
    </svg>
);

const DeepSeekIcon = ({ className }: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d="M12 2L2 7l10 5 10-5-10-5zm0 9l2.5-1.25L12 8.5l-2.5 1.25L12 11zm0 2.5l-5-2.5-5 2.5L12 22l10-8.5-5-2.5-5 2.5z" />
    </svg>
);

export const ModelSelector: React.FC<ModelSelectorProps> = ({ selectedModelId, onSelect }) => {
    const [isOpen, setIsOpen] = useState(false);
    
    const selectedModel = AVAILABLE_MODELS.find(m => m.id === selectedModelId) || AVAILABLE_MODELS[0];

    const getIcon = (model: AiModel) => {
        const className = "w-4 h-4";
        switch (model.provider) {
            case 'Google':
                return model.id.includes('flash') ? <Zap size={14} className="text-amber-500" /> : <Stars size={14} className="text-purple-500" />;
            case 'OpenAI':
                return <OpenAIIcon className={`${className} text-green-600`} />;
            case 'Anthropic':
                return <AnthropicIcon className={`${className} text-orange-600`} />;
            case 'DeepSeek':
                return <DeepSeekIcon className={`${className} text-blue-600`} />;
            default:
                return <Brain size={14} className="text-stone-500" />;
        }
    };

    return (
        <div className="relative">
            <button 
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-2 md:px-3 py-2 bg-white border border-stone-200 rounded-lg hover:bg-stone-50 transition-colors shadow-sm md:min-w-[200px]"
            >
                {getIcon(selectedModel)}
                <div className="flex flex-col items-start flex-1">
                    <span className="text-xs font-bold text-stone-700 leading-none">{selectedModel.name}</span>
                    <span className="text-[9px] text-stone-400 hidden md:block">{selectedModel.provider}</span>
                </div>
                <ChevronDown size={14} className="text-stone-400" />
            </button>

            {isOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl border border-stone-200 shadow-xl z-50 overflow-hidden animate-fade-in-up">
                    <div className="bg-stone-50 px-3 py-2 border-b border-stone-100 text-[10px] font-bold text-stone-400 uppercase tracking-wider flex justify-between">
                        <span>Select Model</span>
                        <span>Vercel Gateway</span>
                    </div>
                    <div className="max-h-[300px] overflow-y-auto">
                        {AVAILABLE_MODELS.map((model) => (
                            <button
                                key={model.id}
                                onClick={() => { onSelect(model.id); setIsOpen(false); }}
                                className="w-full flex items-start gap-3 p-3 hover:bg-stone-50 transition-colors text-left border-b border-stone-100 last:border-0"
                            >
                                <div className="mt-0.5 p-1.5 bg-white border border-stone-100 rounded-md shadow-sm">
                                    {getIcon(model)}
                                </div>
                                <div className="flex-1">
                                    <div className="flex justify-between items-center mb-0.5">
                                        <span className={`text-sm font-bold ${model.id === selectedModelId ? 'text-stone-900' : 'text-stone-600'}`}>
                                            {model.name}
                                        </span>
                                        {model.id === selectedModelId && <Check size={14} className="text-green-600" />}
                                    </div>
                                    <p className="text-[10px] text-stone-500 leading-tight mb-1">{model.description}</p>
                                    <div className="flex gap-1">
                                        {model.isPremium && (
                                            <span className="inline-block px-1.5 py-0.5 bg-stone-900 text-nobel-gold text-[8px] font-bold uppercase rounded">
                                                Pro
                                            </span>
                                        )}
                                        <span className="inline-block px-1.5 py-0.5 bg-stone-100 text-stone-500 text-[8px] font-bold uppercase rounded border border-stone-200">
                                            {(model.contextWindow / 1000)}k ctx
                                        </span>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}
            
            {isOpen && (
                <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)}></div>
            )}
        </div>
    );
};